/**
 * An action's own compute and container settings, as the override fields
 * spell them.
 *
 * The override controls hold strings, because the selects and text inputs they
 * render do; an action holds numbers and lists. Everything that bridges the two
 * lives here, so the fields render what they are handed and decide nothing.
 */

import { type ActionRecord } from "@/shared/domain/actions";

import { determineActionMemoryOptions } from "../components/action/actionUtils";

/**
 * What an invocation runs with when the action declares no compute of its own.
 * These mirror `ComputeRequirements` in the Python SDK, which is where the
 * platform's defaults are declared.
 */
export const PLATFORM_COMPUTE = {
  vCPU: 512,
  memory: 1024,
  storage: 21,
} as const;

export interface ComputeDefaults {
  cpu: string;
  memory: string;
  storage: string;
}

export interface ContainerDefaults {
  workdir: string;
  entrypoint: string;
  command: string;
}

/** The compute an invocation gets if nothing overrides it. */
export function computeDefaultsOf(
  action: ActionRecord | null,
): ComputeDefaults {
  const compute = action?.compute_requirements;
  return {
    cpu: String(compute?.vCPU ?? PLATFORM_COMPUTE.vCPU),
    memory: String(compute?.memory ?? PLATFORM_COMPUTE.memory),
    storage: String(compute?.storage ?? PLATFORM_COMPUTE.storage),
  };
}

/**
 * The parameters an invocation of `action` is refused without. A declared
 * default does not excuse one: the service checks for the required names
 * before it fills defaults in.
 */
export function requiredParametersOf(action: ActionRecord | null): string[] {
  return (action?.parameters ?? [])
    .filter((parameter) => parameter.required)
    .map((parameter) => parameter.name);
}

/**
 * The container settings an invocation gets if nothing overrides them, as
 * placeholder text. A setting the action leaves unset reads as blank rather
 * than as the word "null", which is how the wire spells it.
 */
export function containerDefaultsOf(
  action: ActionRecord | null,
): ContainerDefaults {
  const container = action?.container_parameters;
  return {
    workdir: container?.workdir ?? "",
    // Written the way the fields take them back: one comma-separated line.
    entrypoint: container?.entry_point?.join(", ") ?? "",
    command: container?.command?.join(", ") ?? "",
  };
}

/**
 * `memory` when the vCPU allows that much, and otherwise the nearest amount it
 * does: the smallest allowed value at or above it, or the largest allowed when
 * even that is short.
 *
 * The runtime validates the pair, not each half, so a vCPU change that strands
 * the memory beside it has to move the memory too. A vCPU with no allowed
 * amounts -- one the option table does not know -- leaves the memory alone
 * rather than inventing a value for it.
 */
export function legalMemoryFor(cpu: string, memory: string): string {
  const allowed = determineActionMemoryOptions(cpu);
  if (allowed.length === 0) {
    return memory;
  }
  const wanted = Number.parseInt(memory, 10);
  if (Number.isNaN(wanted)) {
    return String(allowed[0]);
  }
  if (allowed.includes(wanted)) {
    return memory;
  }
  return String(
    allowed.find((amount) => amount >= wanted) ?? allowed[allowed.length - 1],
  );
}
